import React, { useEffect, useState, useCallback } from 'react';
import Skeleton from '../common/Skeleton';
import {
    Plus, Calendar, CircleMinus, CircleCheck, Clock, Search, Save, Check, Edit2, Rocket, Lock
} from 'lucide-react';
import FormControl from '../common/FormControl';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Pagination from '../common/Pagination';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { isUniqueLocal } from '../../validation/uniqueness';
import DualListSelector, { DualListItem } from '../audits/procedure-types/common/DualListSelector';
import GeographyPicker, { GeographySelection, isGeographyComplete } from '../common/GeographyPicker';
import riskAssessmentService, {
    RiskAssessmentTemplate,
    RiskAssessmentTemplateCreatePayload,
    AssessmentTemplateRisk,
} from '../../services/riskAssessmentService';
import riskAssessmentProgramService from '../../services/riskAssessmentProgramService';
import { userService } from '../../services/userService';
import riskRegisterService from '../../services/riskRegisterService';
import extractApiError from '../../utils/apiError';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import TemplateCadenceTab from '../cadence/TemplateCadenceTab';
import TemplateProgramsTab from '../cadence/TemplateProgramsTab';
import { useSubscription } from '../../contexts/SubscriptionContext';
import ApprovalDot, { WorkflowState } from '../common/ApprovalDot';
import WorkflowModal from '../common/WorkflowModal';
import WorkflowSaveBar, { SaveTarget } from '../common/WorkflowSaveBar';
import { usePermissions, usePermission } from '../../contexts/PermissionContext';

const ACT_RAT_TEMPLATE = 'RSK_RAT_TEMPLATES';
const WF_KIND = 'rsk_rat_template';

// =============================================================================
// Create modal (new templates only)
// =============================================================================

type IdentityForm = {
    id?: number;
    assessment_code: string;
    risk_assessment_name: string;
    description?: string;
    is_active?: number;
};

const EMPTY_IDENTITY: IdentityForm = {
    assessment_code: '',
    risk_assessment_name: '',
    description: '',
};

const IdentityModal = ({
    isOpen, onClose, onSubmit, canModify, canApprove, onError, templates,
}: {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (form: IdentityForm, target: SaveTarget) => Promise<void>;
    canModify: boolean;
    canApprove: boolean;
    onError: (msg: string) => void;
    templates: RiskAssessmentTemplate[];
}) => {
    const [formData, setFormData] = useState<IdentityForm>(EMPTY_IDENTITY);
    const [submitting, setSubmitting] = useState(false);
    const { errors, validateField, clearError, setError, validateAll, reset } = useFieldValidation();

    useEffect(() => { setFormData(EMPTY_IDENTITY); setSubmitting(false); reset(); }, [isOpen]);

    const handleSave = async (target: SaveTarget) => {
        const valid = validateAll([
            { name: 'risk_assessment_name', code: 109, value: formData.risk_assessment_name, required: true },
            ...(formData.description ? [{ name: 'description', code: 110, value: formData.description }] : []),
        ]);
        if (!valid) return;
        setSubmitting(true);
        try {
            await onSubmit(formData, target);
            onClose();
        } catch (e: any) {
            const msg: string = e?.response?.data?.detail ?? e?.message ?? 'Create failed';
            if (e?.response?.status === 409 && msg.toLowerCase().includes('code')) {
                setError('assessment_code', msg);
            } else if (e?.response?.status === 409 && msg.toLowerCase().includes('name')) {
                setError('risk_assessment_name', msg);
            } else {
                onError(extractApiError(e, 'Create failed'));
            }
        } finally { setSubmitting(false); }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md">
            <Modal.Header>Create RAT Template</Modal.Header>
            <Modal.Body>
                <form id="assessment-identity-form" onSubmit={(e) => { e.preventDefault(); handleSave('draft'); }} className="flex flex-col gap-y-4" data-testid="MOD_risk_assessment_identity_form">
                    {/* Template Code removed — system code (RAT-N) is auto-generated on approve. */}
                    <FormControl.Input
                        label="Template Name"
                        required
                        value={formData.risk_assessment_name}
                        onChange={e => { setFormData({ ...formData, risk_assessment_name: e.target.value }); clearError('risk_assessment_name'); }}
                        onBlur={(e) => {
                            validateField('risk_assessment_name', 109, e.target.value, { required: true });
                            if (e.target.value.trim() && !isUniqueLocal(933, e.target.value, templates)) {
                                setError('risk_assessment_name', 'A template with this name already exists');
                            }
                        }}
                        error={errors['risk_assessment_name']}
                        data-testid="INP_risk_assessment_identity_name"
                    />
                    <FormControl.Textarea
                        label="Description"
                        value={formData.description || ''}
                        onChange={e => { setFormData({ ...formData, description: e.target.value }); clearError('description'); }}
                        onBlur={(e) => validateField('description', 110, e.target.value)}
                        placeholder="What is this assessment for?"
                        error={errors['description']}
                        data-testid="INP_risk_assessment_identity_description"
                    />
                    <p className="text-xs text-gray-500 italic">
                        After creating, click the record to configure frequency, due-date rule, and risk scope.
                    </p>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <WorkflowSaveBar
                    canDraft={canModify}
                    canSubmit={canModify}
                    canApprove={canApprove}
                    busy={submitting}
                    onSave={handleSave}
                />
            </Modal.Footer>
        </Modal>
    );
};

// =============================================================================
// Configure modal — tabbed: Basic Details | Configure
// =============================================================================

type ConfigTab = 'details' | 'configure' | 'cadence' | 'programs';

const CONFIG_TABS: { key: ConfigTab; label: string }[] = [
    { key: 'details', label: 'Basic Details' },
    { key: 'configure', label: 'Configurations' },
    { key: 'cadence', label: 'Cadence' },
    { key: 'programs', label: 'Rolled-out Programs' },
];

// Migration 114 — UTL_entity_registry id for RISK_ASSESSMENT_TEMPLATE.
// Sourced from the seed in migrations/versions/114_*.sql.
const RAT_TEMPLATE_TYPE_ID = 33;
const RAT_TEMPLATE_TYPE_CODE = 'RISK_ASSESSMENT_TEMPLATE';
const RAT_TEMPLATE_DISPLAY = 'Risk Assessment Template';

const ConfigureModal = ({
    isOpen, onClose, template, onSaved,
}: {
    isOpen: boolean;
    onClose: () => void;
    template: RiskAssessmentTemplate;
    onSaved: () => void;
}) => {
    const [activeTab, setActiveTab] = useState<ConfigTab>('details');
    useEffect(() => { if (isOpen) setActiveTab('details'); }, [isOpen]);

    // RBAC (extent-only): in-context cadence tab is gated by SCH_RSK_CADENCE.
    const { check: checkPerm } = usePermissions();
    const canModifyCadence = checkPerm('SCH_RSK_CADENCE', 'view+modify').allowed;

    // Subscription: hide the in-context Cadence tab when the Cadence module is locked.
    const { isModuleLocked } = useSubscription();
    const cfgTabs = isModuleLocked('cadence')
        ? CONFIG_TABS.filter(t => t.key !== 'cadence')
        : CONFIG_TABS;

    const [toast, setToast] = useState<ToastMessage | null>(null);

    // Approved templates are frozen: no field edits, no toggle. Unlock via the
    // status dot's Reopen action.
    const isApproved = (template as any).workflow_state === 'approved';

    // ---- Basic Details tab ----
    const [detailsForm, setDetailsForm] = useState({
        risk_assessment_name: template.risk_assessment_name,
        description: template.description || '',
    });
    const [localActive, setLocalActive] = useState(template.is_active ?? 1);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const { errors: detailsErrors, validateField: detailsValidateField, clearError: detailsClearError, setError: detailsSetError, validateAll: detailsValidateAll, reset: detailsReset } = useFieldValidation();
    const { errors: cfgErrors, validateField: cfgValidateField, clearError: cfgClearError, validateAll: cfgValidateAll, reset: cfgReset } = useFieldValidation();

    const hasChanges = detailsForm.risk_assessment_name !== template.risk_assessment_name
        || detailsForm.description !== (template.description || '');

    useEffect(() => {
        if (!isOpen) return;
        setDetailsForm({
            risk_assessment_name: template.risk_assessment_name,
            description: template.description || '',
        });
        setLocalActive(template.is_active ?? 1);
        setEditMode(false);
        detailsReset();
        cfgReset();
    }, [isOpen, template]);

    const handleUpdateDetails = async (e: React.FormEvent) => {
        e.preventDefault();
        const valid = detailsValidateAll([
            { name: 'risk_assessment_name', code: 109, value: detailsForm.risk_assessment_name, required: true },
            ...(detailsForm.description ? [{ name: 'description', code: 110, value: detailsForm.description }] : []),
        ]);
        if (!valid) return;
        setSubmitting(true); setToast(null);
        try {
            await riskAssessmentService.update(template.id, {
                risk_assessment_name: detailsForm.risk_assessment_name,
                description: detailsForm.description,
            });
            onSaved();
            setEditMode(false);
            setToast({ type: 'success', message: 'Template updated successfully.' });
        } catch (err: any) {
            const msg: string = err?.response?.data?.detail ?? err?.message ?? 'Update failed';
            if (err?.response?.status === 409 && msg.toLowerCase().includes('code')) {
                detailsSetError('assessment_code', msg);
            } else if (err?.response?.status === 409 && msg.toLowerCase().includes('name')) {
                detailsSetError('risk_assessment_name', msg);
            } else {
                setToast({ type: 'error', message: extractApiError(err, 'Update failed') });
            }
        } finally { setSubmitting(false); }
    };

    const handleToggle = async () => {
        const next = localActive ? 0 : 1;
        const confirmFn = next ? confirmActive : confirmWarning;
        const ok = await confirmFn({
            title: next ? 'Reactivate this template?' : 'Inactivate this template?',
            text: next ? 'It will appear in active lists again.' : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            await riskAssessmentService.update(template.id, { is_active: next });
            setLocalActive(next);
            onSaved();
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated.' : 'Inactivated.' });
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Toggle failed') });
        } finally { setToggling(false); }
    };

    // ---- Configure tab ----
    const [frequency, setFrequency] = useState(template.frequency || '');
    const [dueRule, setDueRule] = useState(template.due_date_program || '');
    const [allRisks, setAllRisks] = useState<{ id: number; name: string }[]>([]);
    const [selected, setSelected] = useState<DualListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isOpen || activeTab !== 'configure') return;
        let cancelled = false;
        setLoading(true);
        setFrequency(template.frequency || '');
        setDueRule(template.due_date_program || '');
        Promise.all([
            // Only ACTIVE + APPROVED risks are eligible for a template's scope.
            riskRegisterService.getDropdown(),
            riskAssessmentService.listRisks(template.id),
        ]).then(([risks, scope]) => {
            if (cancelled) return;
            setAllRisks(risks);
            setSelected(scope.map((r: AssessmentTemplateRisk) => ({
                id: r.id,
                title: r.risk_name,
            })));
        }).catch(e => {
            if (!cancelled) setToast({ type: 'error', message: extractApiError(e, 'Failed to load configuration') });
        }).finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen, activeTab, template.id, template.frequency, template.due_date_program]);

    // Newest-first (id DESC) to mirror the Risk Register list order, so the same
    // top risks line up between the two screens.
    const scopeItems: DualListItem[] = allRisks
        .slice()
        .sort((a, b) => b.id - a.id)
        .map(r => ({ id: r.id, title: r.name }));

    const handleSaveConfigure = async () => {
        // Approved templates are frozen — mirror the Basic Details tab lock and
        // every other workflow "approved" case: block the save and surface why
        // via a toast. Reopen via the status dot to edit.
        if (isApproved) {
            setToast({ type: 'error', message: 'Record is approved and locked. Reopen via the workflow button to edit.' });
            return;
        }
        const pairs: any[] = [];
        if (frequency.trim()) pairs.push({ name: 'frequency', code: 109, value: frequency });
        if (dueRule.trim()) pairs.push({ name: 'due_date_rule', code: 109, value: dueRule });
        if (pairs.length > 0) {
            const valid = cfgValidateAll(pairs);
            if (!valid) return;
        }
        setSaving(true); setToast(null);
        try {
            if (frequency !== (template.frequency || '') || dueRule !== (template.due_date_program || '')) {
                await riskAssessmentService.update(template.id, {
                    frequency: frequency || undefined,
                    due_date_program: dueRule || undefined,
                });
            }
            await riskAssessmentService.replaceScope(template.id, selected.map(s => Number(s.id)));
            onSaved();
            setToast({ type: 'success', message: 'Configuration saved.' });
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to save configuration') });
        } finally { setSaving(false); }
    };

    const anyBusy = submitting || toggling || saving;

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" closeOnOverlayClick={false}>
            <Modal.Header>
                {isApproved && activeTab === 'details' ? (
                    <>Viewing (locked) "<span className="item-highlight">{template.risk_assessment_name}</span>"</>
                ) : (
                    <>Configure RAT Template — {template.code ? `${template.code} · ` : ''}{template.risk_assessment_name}</>
                )}
            </Modal.Header>

            <Modal.Body>
                <div className="app-modal-tabs">
                    {cfgTabs.map(t => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setActiveTab(t.key)}
                            className={`app-modal-tab${activeTab === t.key ? ' app-modal-tab--active' : ''}`}
                            data-testid={`TAB_risk_assessment_configure_${t.key}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {activeTab === 'cadence' ? (
                    <TemplateCadenceTab
                        sourceTypeId={RAT_TEMPLATE_TYPE_ID}
                        sourceRecordId={template.id}
                        sourceTypeCode={RAT_TEMPLATE_TYPE_CODE}
                        sourceName={template.risk_assessment_name}
                        sourceDisplayName={RAT_TEMPLATE_DISPLAY}
                        canModify={canModifyCadence}
                    />
                ) : activeTab === 'programs' ? (
                    <TemplateProgramsTab
                        sourceTypeId={RAT_TEMPLATE_TYPE_ID}
                        sourceRecordId={template.id}
                        sourceTypeCode={RAT_TEMPLATE_TYPE_CODE}
                    />
                ) : activeTab === 'details' ? (
                    <form id="assessment-details-form" onSubmit={handleUpdateDetails} className="flex flex-col gap-y-4 pt-4" data-testid="MOD_risk_assessment_details_form">
                        {isApproved && (
                            <div className="approved-banner" data-testid="BANNER_risk_assessment_locked">
                                <Lock size={14} /> Record is approved and locked. Reopen via the workflow button to edit.
                            </div>
                        )}
                        {/* System-generated code (RAT-N) — read-only, never editable.
                            Shows "—" until the template is approved. */}
                        <FormControl.Input
                            label="Code"
                            value={template.code || '—'}
                            disabled
                            data-testid="INP_risk_assessment_details_code"
                        />
                        <FormControl.Input
                            label="Template Name"
                            required
                            value={detailsForm.risk_assessment_name}
                            onChange={e => { setDetailsForm({ ...detailsForm, risk_assessment_name: e.target.value }); detailsClearError('risk_assessment_name'); }}
                            onBlur={e => detailsValidateField('risk_assessment_name', 109, e.target.value, { required: true })}
                            disabled={!editMode}
                            error={detailsErrors['risk_assessment_name']}
                            data-testid="INP_risk_assessment_details_name"
                        />
                        <FormControl.Textarea
                            label="Description"
                            value={detailsForm.description}
                            onChange={e => { setDetailsForm({ ...detailsForm, description: e.target.value }); detailsClearError('description'); }}
                            onBlur={e => detailsValidateField('description', 110, e.target.value)}
                            disabled={!editMode}
                            error={detailsErrors['description']}
                            data-testid="INP_risk_assessment_details_description"
                        />
                    </form>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-4 mb-4 pt-4">
                            <FormControl.Input
                                label="Frequency"
                                value={frequency}
                                onChange={e => { setFrequency(e.target.value); cfgClearError('frequency'); }}
                                onBlur={e => cfgValidateField('frequency', 109, e.target.value)}
                                disabled={isApproved}
                                placeholder="e.g. Annual"
                                error={cfgErrors['frequency']}
                                data-testid="INP_risk_assessment_configure_frequency"
                            />
                            <FormControl.Input
                                label="Due Date Rule"
                                value={dueRule}
                                onChange={e => { setDueRule(e.target.value); cfgClearError('due_date_rule'); }}
                                onBlur={e => cfgValidateField('due_date_rule', 109, e.target.value)}
                                disabled={isApproved}
                                placeholder="e.g. End of fiscal year"
                                error={cfgErrors['due_date_rule']}
                                data-testid="INP_risk_assessment_configure_due_rule"
                            />
                        </div>
                        <p className="text-sm font-semibold text-gray-700 mb-2">Scope — Risks covered</p>
                        <div style={{ height: '300px' }}>
                            {loading ? (
                                <div className="h-full flex items-center justify-center text-gray-500">Loading…</div>
                            ) : (
                                <DualListSelector
                                    availableItems={scopeItems}
                                    selectedItems={selected}
                                    onSelectionChange={setSelected}
                                    disabled={isApproved}
                                    titleLeft="Available Risks"
                                    titleRight="Risks in Template"
                                    searchPlaceholder="Search risks…"
                                    emptyMessageLeft="No more risks available"
                                    emptyMessageRight="No risks selected"
                                />
                            )}
                        </div>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer className={activeTab === 'details' && !isApproved ? 'app-modal-footer--split' : ''}>
                {activeTab === 'details' && !isApproved ? (
                    <>
                        <Button
                            variant={localActive ? 'inactive-outline' : 'active-outline'}
                            icon={localActive ? CircleMinus : CircleCheck}
                            onClick={handleToggle}
                            disabled={anyBusy}
                            data-testid="BTN_risk_assessment_details_toggle"
                        >
                            {toggling ? '...' : localActive ? 'Inactive' : 'Active'}
                        </Button>
                        {editMode ? (
                            <Button
                                type="submit"
                                form="assessment-details-form"
                                variant="update"
                                icon={Check}
                                disabled={anyBusy || !hasChanges}
                                data-testid="BTN_risk_assessment_details_update"
                            >
                                {submitting ? 'Updating...' : 'Update'}
                            </Button>
                        ) : (
                            <Button
                                variant="fill"
                                icon={Edit2}
                                onClick={() => setEditMode(true)}
                                disabled={anyBusy}
                                data-testid="BTN_risk_assessment_details_edit"
                            >
                                Edit
                            </Button>
                        )}
                    </>
                ) : activeTab === 'configure' ? (
                    <Button
                        variant="update"
                        icon={Check}
                        onClick={handleSaveConfigure}
                        disabled={saving || loading}
                        className={isApproved ? 'opacity-50 cursor-not-allowed' : ''}
                        title={isApproved ? 'Record is approved and locked. Reopen via the workflow button to edit.' : undefined}
                        data-testid="BTN_risk_assessment_configure_save"
                    >
                        {saving ? 'Updating…' : 'Update'}
                    </Button>
                ) : null}
            </Modal.Footer>

            <Toast toast={toast} onClose={() => setToast(null)} />
        </Modal>
    );
};

// =============================================================================
// Roll Out modal
// =============================================================================

const RolloutModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    template: RiskAssessmentTemplate;
    onRolledOut: () => void;
    onError: (msg: string) => void;
}> = ({ isOpen, onClose, template, onRolledOut, onError }) => {
    const [users, setUsers] = useState<{ id: number; name: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [makerId, setMakerId] = useState('');
    const [checkerId, setCheckerId] = useState('');
    const [geography, setGeography] = useState<GeographySelection>({ mode: 'locations', location_ids: [] });
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setLoading(true);
        setMakerId(''); setCheckerId(''); setGeography({ mode: 'locations', location_ids: [] });
        setStartDate(''); setEndDate('');
        userService.getUsersDropdown()
            .then(u => { if (!cancelled) setUsers(u.map(x => ({ id: x.id, name: x.name }))); })
            .catch(e => { if (!cancelled) onError(extractApiError(e, 'Failed to load users')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // Only reset/reload when the modal opens. `onError` is an inline
        // callback recreated on every parent render (e.g. when a toast fires),
        // so including it here would re-run this effect and wipe the user's
        // in-progress inputs whenever an error toast is shown.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!makerId || !checkerId || !startDate || !endDate || !isGeographyComplete(geography)) {
            onError('Maker, checker, geography, start date and end date are required.');
            return;
        }
        if (endDate < startDate) { onError('End date must be on or after start date.'); return; }
        setSaving(true);
        try {
            await riskAssessmentProgramService.rollout(template.id, {
                maker_id: parseInt(makerId, 10),
                checker_id: parseInt(checkerId, 10),
                geography,
                start_date: startDate,
                end_date: endDate,
            });
            onRolledOut();
            onClose();
        } catch (err: any) {
            onError(extractApiError(err, 'Failed to roll out program.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnOverlayClick={false}>
            <Modal.Header>
                Roll Out RAT — "<span className="item-highlight">{template.risk_assessment_name}</span>"
            </Modal.Header>
            <Modal.Body>
                <form id="rsk-rollout-form" onSubmit={submit} className="flex flex-col gap-y-4">
                    {makerId !== '' && makerId === checkerId && (
                        <div className="warning-banner" role="status"
                             data-testid="WRN_rollout_same_maker_checker">
                            Maker and checker are the same person. This assessment will be performed and
                            approved by one individual — permitted, and recorded as self-reviewed.
                        </div>
                    )}
                    <FormControl.SearchSelect
                        label="Maker (executor)"
                        required
                        options={users.map(u => ({ value: u.id, label: u.name }))}
                        value={makerId}
                        onChange={v => setMakerId(v == null ? '' : String(v))}
                        disabled={loading}
                        placeholder="Select user…"
                        data-testid="SEL_rsk_rollout_maker"
                    />
                    <FormControl.SearchSelect
                        label="Checker (approver)"
                        required
                        options={users.map(u => ({ value: u.id, label: u.name }))}
                        value={checkerId}
                        onChange={v => setCheckerId(v == null ? '' : String(v))}
                        disabled={loading}
                        placeholder="Select user…"
                        data-testid="SEL_rsk_rollout_checker"
                    />
                    <div>
                        <label className="fg__label">Geography <span className="fg__required" aria-hidden="true">*</span></label>
                        <GeographyPicker value={geography} onChange={setGeography} disabled={loading} />
                    </div>
                    <div className="grid grid-cols-2 gap-x-4">
                        <FormControl.DatePicker label="Start Date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        <FormControl.DatePicker label="End Date" required value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                    </div>
                </form>
            </Modal.Body>
            <Modal.Footer>
                <Button type="submit" form="rsk-rollout-form" variant="fill" icon={Rocket} disabled={saving || loading}>
                    {saving ? 'Rolling out…' : 'Roll Out'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// =============================================================================
// Main
// =============================================================================

const RiskAssessments = () => {
    const [templates, setTemplates] = useState<RiskAssessmentTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [isIdentityOpen, setIsIdentityOpen] = useState(false);
    const [isConfigureOpen, setIsConfigureOpen] = useState(false);
    const [isRolloutOpen, setIsRolloutOpen] = useState(false);
    const [activeTemplate, setActiveTemplate] = useState<RiskAssessmentTemplate | undefined>(undefined);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('active');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [workflowRow, setWorkflowRow] = useState<RiskAssessmentTemplate | null>(null);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const data = await riskAssessmentService.list();
            setTemplates(data);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load templates') });
        } finally {
            setLoading(false);
        }
    }, []);

    const { allowed: canView, loading: viewLoading } = usePermission(ACT_RAT_TEMPLATE, 'view');
    const { check } = usePermissions();
    const canModify = check(ACT_RAT_TEMPLATE, 'view+modify').allowed;
    const canApproveExtent = check(ACT_RAT_TEMPLATE, 'view+modify+approve').allowed;

    useEffect(() => { if (canView) refresh(); }, [refresh, canView]);
    useEffect(() => { setPage(1); }, [searchTerm, statusFilter, pageSize]);

    const handleCreate = () => setIsIdentityOpen(true);

    const handleSubmitIdentity = async (form: IdentityForm, target: SaveTarget) => {
        const payload: RiskAssessmentTemplateCreatePayload = {
            assessment_code: form.assessment_code,
            risk_assessment_name: form.risk_assessment_name,
            description: form.description,
        };
        await riskAssessmentService.create(payload, target);
        const msg = { draft: 'Template saved as draft.', submit: 'Template submitted for approval.', approve: 'Template created and approved.' }[target];
        setToast({ type: 'success', message: msg });
        await refresh();
    };

    const filtered = templates.filter(t => {
        if (statusFilter === 'active' && t.is_active !== 1) return false;
        if (statusFilter === 'inactive' && t.is_active !== 0) return false;
        const q = searchTerm.toLowerCase();
        return t.risk_assessment_name.toLowerCase().includes(q) || (t.code || '').toLowerCase().includes(q);
    });
    const totalRecords = filtered.length;
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    if (!viewLoading && !canView) {
        return (
            <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
                <NoDataFound title="Not authorised" message="You do not have access to 'RSK_RAT_TEMPLATES'." />
            </div>
        );
    }

    return (
        <div className="netbody-card flex flex-col h-full">

            <div className="netbody-header">
                {/* Status — iOS-style on/off switch (on = active) */}
                <StatusSwitch
                    value={statusFilter === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
                    onChange={next => setStatusFilter(next === 'INACTIVE' ? 'inactive' : 'active')}
                    noun="assessments"
                    testId="SEL_risk_assessment_status_filter"
                />
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search templates…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_risk_assessment_search"
                    />
                    {canModify && (
                        <Button variant="fill" icon={Plus} onClick={handleCreate} data-testid="BTN_risk_assessment_create">Create</Button>
                    )}
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <div className="table-list">
                        {!loading && filtered.length === 0 ? (
                            <NoDataFound title="No Templates Yet" message='Click "Create" to add one.' />
                        ) : (
                        <div className="table-list-scroll">
                            <table className="table-list-table">
                                <thead>
                                    <tr>
                                        <th className="table-list-th table-list-cell--dot" aria-label="Workflow status"></th>
                                        <th className="table-list-th table-list-cell--sno">S.No</th>
                                        <th className="table-list-th">Code</th>
                                        <th className="table-list-th">Template</th>
                                        <th className="table-list-th">Frequency</th>
                                        <th className="table-list-th">Risks</th>
                                        <th className="table-list-th table-list-th--right">Roll Out</th>
                                    </tr>
                                </thead>
                                <tbody aria-busy={loading} aria-live="polite">
                                    {loading && <Skeleton.TableRows cols={['dot', '3xs', 'sm', 'md', 'sm', 'pill', { width: 'sm', align: 'right' }]} />}
                                    {!loading && paginatedRows.map((t, idx) => {
                                        const isConfigured = !!(t.frequency || t.due_date_program || (t.risk_count ?? 0) > 0);
                                        return (
                                            <tr
                                                key={t.id}
                                                className="table-list-row"
                                                onClick={() => { setActiveTemplate(t); setIsConfigureOpen(true); }}
                                                data-testid={`BTN_risk_assessment_row_${t.id}_open`}
                                            >
                                                <td
                                                    className="table-list-cell table-list-cell--dot"
                                                    onClick={(e) => { e.stopPropagation(); setWorkflowRow(t); }}
                                                    title="Workflow — view timeline & act"
                                                    data-testid={`BTN_risk_assessment_row_${t.id}_workflow`}
                                                >
                                                    <ApprovalDot
                                                        state={((t.workflow_state) || 'draft') as WorkflowState}
                                                        onClick={() => setWorkflowRow(t)}
                                                        title="Workflow — view timeline & act"
                                                    />
                                                </td>
                                                <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                                <td className="table-list-cell">
                                                    {t.code
                                                        ? <span className="cell-code">{t.code}</span>
                                                        : <span className="text-muted text-xs">—</span>}
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className="table-list-cell-title cell-name-lock">
                                                        {t.workflow_state === 'approved' && (
                                                            <Lock className="lock-icon lock-icon--sm" aria-label="Locked — approved" />
                                                        )}
                                                        {t.risk_assessment_name}
                                                    </span>
                                                    {t.description && <span className="table-list-cell-sub">{t.description}</span>}
                                                </td>
                                                <td className="table-list-cell">
                                                    <div className="flex flex-col gap-0.5 text-sm">
                                                        <span className="flex items-center gap-1">
                                                            <Calendar size={12} className="text-slate-400" />
                                                            {t.frequency || <span className="italic text-slate-400">not set</span>}
                                                        </span>
                                                        {t.due_date_program && (
                                                            <span className="flex items-center gap-1">
                                                                <Clock size={12} className="text-slate-400" />
                                                                {t.due_date_program}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="table-list-cell">
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isConfigured ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500'}`}>
                                                        {t.risk_count ?? 0} risks
                                                    </span>
                                                </td>
                                                <td className="table-list-cell" style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                                                    {(() => {
                                                        const notApproved = t.workflow_state !== 'approved';
                                                        const noRisks = (t.risk_count ?? 0) === 0;
                                                        const disabled = notApproved || noRisks;
                                                        const title = notApproved
                                                            ? 'Template must be approved before roll out'
                                                            : noRisks
                                                                ? 'Template has no risks — configure scope first'
                                                                : 'Roll Out';
                                                        return (
                                                    <Button
                                                        variant="secondary-outline"
                                                        icon={Rocket}
                                                        title={title}
                                                        style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                                                        onClick={(e: React.MouseEvent) => {
                                                            e.stopPropagation();
                                                            if (notApproved) {
                                                                setToast({ type: 'error', message: 'Template must be approved before it can be rolled out.' });
                                                                return;
                                                            }
                                                            if (noRisks) {
                                                                setToast({ type: 'error', message: 'Template has no risks. Configure scope first.' });
                                                                return;
                                                            }
                                                            setActiveTemplate(t);
                                                            setIsRolloutOpen(true);
                                                        }}
                                                        data-testid={`BTN_risk_assessment_row_${t.id}_rollout`}
                                                    >
                                                        Rollout
                                                    </Button>
                                                        );
                                                    })()}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        )}
                        <Pagination
                            page={page}
                            pageSize={pageSize}
                            totalRecords={totalRecords}
                            onPageChange={setPage}
                            onPageSizeChange={setPageSize}
                            recordLabel="templates"
                        />
                    </div>
                </div>
            </div>

            <IdentityModal
                isOpen={isIdentityOpen}
                onClose={() => setIsIdentityOpen(false)}
                onSubmit={handleSubmitIdentity}
                canModify={canModify}
                canApprove={canApproveExtent}
                onError={(msg) => setToast({ type: 'error', message: msg })}
                templates={templates}
            />

            {activeTemplate && (
                <ConfigureModal
                    isOpen={isConfigureOpen}
                    onClose={() => setIsConfigureOpen(false)}
                    template={activeTemplate}
                    onSaved={refresh}
                />
            )}

            {activeTemplate && (
                <RolloutModal
                    isOpen={isRolloutOpen}
                    onClose={() => setIsRolloutOpen(false)}
                    template={activeTemplate}
                    onRolledOut={() => {
                        setToast({ type: 'success', message: 'Risk assessment program rolled out.' });
                        refresh();
                    }}
                    onError={(msg) => setToast({ type: 'error', message: msg })}
                />
            )}

            <WorkflowModal
                isOpen={!!workflowRow}
                onClose={() => setWorkflowRow(null)}
                kind={WF_KIND}
                id={workflowRow?.id ?? null}
                onChanged={refresh}
            />

            <Toast toast={toast} onClose={() => setToast(null)} />
        </div>
    );
};

export default RiskAssessments;
